/**
 * Writing a note on a zone: the words, the language (which sets reading
 * direction - Arabic, Hebrew, Persian and Urdu run right to left), the font,
 * size, colour, weight, alignment, the shape the text is fitted into (an
 * upright box by default since 2026-10-10; the diamond of brief s.11 before),
 * how far it is turned, and the veil that keeps it readable over a picture.
 */
import { DEFAULT_TEXT, FONTS, isRtl, noteShape, type FontKey, type TextLayout, type ZoneText } from '@foldmycard/scene';
import { t } from './strings.ts';

export const LANGUAGES: { code: string; name: string }[] = [
  { code: 'en', name: 'English' }, { code: 'fr', name: 'Français' }, { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' }, { code: 'it', name: 'Italiano' }, { code: 'pt', name: 'Português' },
  { code: 'nl', name: 'Nederlands' }, { code: 'tr', name: 'Türkçe' }, { code: 'ar', name: 'العربية' },
  { code: 'he', name: 'עברית' }, { code: 'fa', name: 'فارسی' }, { code: 'ur', name: 'اردو' },
];
const COLOURS = ['#1b2430', '#ffffff', '#7a1f3d', '#0f6f8c', '#1d7a46', '#b8860b'];

export function defaultLanguage(): string {
  const want = (navigator.language || 'en').split('-')[0];
  return LANGUAGES.some((l) => l.code === want) ? want : 'en';
}

/** A new, empty note: it takes its frame's own shape (owner, 2026-10-17: "always"). */
export function newNote(): ZoneText {
  return { content: '', shape: DEFAULT_TEXT.shape, language: defaultLanguage() };
}

interface Props {
  note: ZoneText;
  layout: TextLayout | undefined;
  hasPicture: boolean;
  onChange: (note: ZoneText) => void;
  onRemove: () => void;
}

export function NoteEditor({ note, layout, hasPicture, onChange, onRemove }: Props) {
  const set = (patch: Partial<ZoneText>) => onChange({ ...note, ...patch });
  const rtl = isRtl(note.language);
  const size = note.sizeMm ?? DEFAULT_TEXT.sizeMm;
  const colour = note.color ?? DEFAULT_TEXT.color;
  const align = note.align ?? DEFAULT_TEXT.align;
  const shape = noteShape(note);
  const turn = Math.round(note.rotationDeg ?? 0);
  /** Turn to `deg`, kept within -180..180; straight is stored as no turn at all. */
  const turnTo = (deg: number) => {
    const d = ((((deg + 180) % 360) + 360) % 360) - 180;
    const { rotationDeg: _, ...rest } = note;
    onChange(d === 0 ? rest : { ...rest, rotationDeg: d });
  };
  return (
    <div className="note-editor">
      <textarea value={note.content} dir={rtl ? 'rtl' : 'auto'} rows={3} placeholder={t('notePlaceholder')}
        aria-label={t('noteText')} onChange={(e) => set({ content: e.target.value })}
        style={{ fontFamily: FONTS[(note.font as FontKey) || 'sans']?.css }} />
      {layout?.overflow && <p className="error small">{t('noteTooLong')}</p>}
      {layout && !layout.overflow && layout.sizeMm < size - 0.05 && (
        <p className="muted small">{t('noteShrunk')}</p>
      )}

      <div className="note-row">
        <label>{t('language')}
          <select value={note.language} onChange={(e) => set({ language: e.target.value })}>
            {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </label>
        <label>{t('font')}
          <select value={note.font || 'sans'} onChange={(e) => set({ font: e.target.value })}>
            {Object.entries(FONTS).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
          </select>
        </label>
      </div>

      <label className="zoom">{t('textSize')}
        <input type="range" min={3} max={16} step={0.5} value={size} onChange={(e) => set({ sizeMm: Number(e.target.value) })} />
        <span className="small muted">{size} mm</span>
      </label>

      <label className="zoom">{t('textTurn')}
        <input type="range" min={-180} max={180} step={1} value={turn} onChange={(e) => turnTo(Number(e.target.value))} />
        <span className="small muted">{turn}°</span>
      </label>
      <div className="note-row">
        <span className="seg" role="group" aria-label={t('textTurn')}>
          <button onClick={() => turnTo(turn - 90)} title={t('turnLeft')}>↺ 90°</button>
          <button onClick={() => turnTo(turn - 15)} title={t('turnLeft')}>↺ 15°</button>
          <button onClick={() => turnTo(turn + 15)} title={t('turnRight')}>↻ 15°</button>
          <button onClick={() => turnTo(turn + 90)} title={t('turnRight')}>↻ 90°</button>
        </span>
        <button onClick={() => turnTo(0)} disabled={turn === 0}>{t('textStraight')}</button>
      </div>

      <div className="note-row tools">
        <span className="seg" role="group" aria-label={t('style')}>
          <button aria-pressed={!!note.bold} onClick={() => set({ bold: !note.bold })} title={t('bold')}><b>B</b></button>
          <button aria-pressed={!!note.italic} onClick={() => set({ italic: !note.italic })} title={t('italic')}><i>I</i></button>
        </span>
        <span className="seg" role="group" aria-label={t('alignment')}>
          {(['start', 'center', 'end'] as const).map((a) => (
            <button key={a} aria-pressed={align === a} onClick={() => set({ align: a })} title={t(`align_${a}`)}>
              {a === 'center' ? '≡' : (a === 'start') !== rtl ? '⇤' : '⇥'}
            </button>
          ))}
        </span>
        <span className="swatches" role="group" aria-label={t('colour')}>
          {COLOURS.map((c) => (
            <button key={c} className="swatch" style={{ background: c }} aria-pressed={colour.toLowerCase() === c}
              aria-label={c} onClick={() => set({ color: c })} />
          ))}
          <input type="color" value={colour} aria-label={t('otherColour')} onChange={(e) => set({ color: e.target.value })} />
        </span>
      </div>

      <div className="note-row">
        <span className="seg" role="group" aria-label={t('shape')}>
          <button aria-pressed={shape === 'zone'} onClick={() => { const { shapeChosen: _, ...rest } = note; onChange({ ...rest, shape: 'zone' }); }}>⬠ {t('shapeZone')}</button>
          <button aria-pressed={shape === 'square'} onClick={() => set({ shape: 'square', shapeChosen: true })}>▢ {t('shapeSquare')}</button>
          <button aria-pressed={shape === 'diamond'} onClick={() => set({ shape: 'diamond', shapeChosen: true })}>◆ {t('shapeDiamond')}</button>
        </span>
      </div>

      {hasPicture && (
        <label className="zoom">{t('veil')}
          <input type="range" min={0} max={100} step={1} value={Math.round((note.backdrop ?? DEFAULT_TEXT.backdrop) * 100)}
            onChange={(e) => set({ backdrop: Number(e.target.value) / 100 })} />
          <span className="small muted">{Math.round((note.backdrop ?? DEFAULT_TEXT.backdrop) * 100)}%</span>
        </label>
      )}
      <button onClick={onRemove}>{t('removeNote')}</button>
    </div>
  );
}
